import React, { useState, useRef, useEffect } from 'react';
import { aiChat, aiPredictDelays, aiSummary, aiSearch, createTask } from '../lib/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle
} from '../components/ui/sheet';
import {
  Send, Bot, User, Sparkles, AlertTriangle, FileText, Search,
  Loader2, Trash2, CheckCircle2, Plus
} from 'lucide-react';
import { toast } from 'sonner';

const QuickAction = ({ icon: Icon, label, onClick, loading }) => (
  <button
    onClick={onClick}
    disabled={loading}
    className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-sm hover:bg-accent hover:border-[#F5A623]/50 transition-all disabled:opacity-50"
    data-testid={`ai-action-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <Icon className="h-3.5 w-3.5 text-[#F5A623]" />
    <span>{label}</span>
  </button>
);

const MarkdownRenderer = ({ content }) => {
  // Simple markdown rendering for bold, lists, tables, and headers
  const lines = content.split('\n');
  return (
    <div className="prose prose-sm dark:prose-invert max-w-none text-sm leading-relaxed">
      {lines.map((line, i) => {
        // Headers
        if (line.startsWith('### ')) return <h4 key={i} className="font-semibold text-sm mt-3 mb-1">{line.slice(4)}</h4>;
        if (line.startsWith('## ')) return <h3 key={i} className="font-semibold text-base mt-3 mb-1">{line.slice(3)}</h3>;
        if (line.startsWith('# ')) return <h2 key={i} className="font-bold text-lg mt-3 mb-1">{line.slice(2)}</h2>;
        
        // Table rows
        if (line.startsWith('|')) {
          if (line.match(/^\|[\s-|]+$/)) return null; // skip separator
          const cells = line.split('|').filter(Boolean).map(c => c.trim());
          return (
            <div key={i} className="flex gap-2 py-1 text-xs border-b border-border/50">
              {cells.map((cell, j) => (
                <span key={j} className="flex-1 truncate">{renderInline(cell)}</span>
              ))}
            </div>
          );
        }
        
        // Bullet points
        if (line.match(/^[-*]\s/)) {
          return <div key={i} className="flex gap-2 ml-2 my-0.5"><span className="text-[#F5A623]">-</span><span>{renderInline(line.slice(2))}</span></div>;
        }
        if (line.match(/^\d+\.\s/)) {
          const num = line.match(/^(\d+)\.\s/)[1];
          return <div key={i} className="flex gap-2 ml-2 my-0.5"><span className="text-[#F5A623] font-medium">{num}.</span><span>{renderInline(line.slice(num.length + 2))}</span></div>;
        }
        
        // Empty line
        if (!line.trim()) return <div key={i} className="h-2" />;
        
        // Regular text
        return <p key={i} className="my-0.5">{renderInline(line)}</p>;
      })}
    </div>
  );
};

function renderInline(text) {
  // Bold
  return text.split(/(\*\*[^*]+\*\*)/).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

export const AIAssistantPanel = ({ open, onOpenChange }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const addMessage = (role, content, type = 'chat', extra = {}) => {
    setMessages(prev => [...prev, { role, content, type, timestamp: new Date().toISOString(), ...extra }]);
  };

  const handleConfirmCreateTask = async (idx, proposal) => {
    try {
      const payload = {
        task_name: proposal.task_name,
        due_date: proposal.due_date,
        priority: proposal.priority || 5,
        category: proposal.category || '',
        sub_category: proposal.sub_category || '',
        brand: proposal.brand || '',
        description: proposal.description || '',
        assigned_to: proposal.assigned_to,
        department: proposal.department,
        status: proposal.status || 'To Do',
        work_type: proposal.work_type || 'Individual',
        assigned_to_list: proposal.assigned_to_list || [],
        is_recurring: !!proposal.is_recurring,
        repeat_type: proposal.repeat_type || '',
      };
      const res = await createTask(payload);
      setMessages(prev => prev.map((m, i) => i === idx ? { ...m, taskCreated: true, createdTaskId: res.data?.id } : m));
      toast.success(`Task "${proposal.task_name}" created`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;
    
    const userMsg = input.trim();
    setInput('');
    addMessage('user', userMsg);
    setLoading(true);
    
    try {
      const response = await aiChat({ message: userMsg });
      addMessage('assistant', response.data.response, 'chat', { proposedTask: response.data.proposed_task || null });
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to get AI response';
      addMessage('assistant', `Sorry, I encountered an error: ${detail}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePredictDelays = async () => {
    addMessage('user', 'Analyze my tasks and predict potential delays', 'action');
    setLoading(true);
    try {
      const response = await aiPredictDelays();
      addMessage('assistant', response.data.analysis, 'delays');
    } catch (error) {
      addMessage('assistant', 'Failed to predict delays. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSummary = async () => {
    addMessage('user', 'Generate a summary of current task status', 'action');
    setLoading(true);
    try {
      const response = await aiSummary({ scope: 'department' });
      addMessage('assistant', response.data.summary, 'summary');
    } catch (error) {
      addMessage('assistant', 'Failed to generate summary. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!input.trim()) {
      toast.error('Type a search query first');
      return;
    }
    const query = input.trim();
    setInput('');
    addMessage('user', `Search: ${query}`, 'action');
    setLoading(true);
    try {
      const response = await aiSearch({ message: query });
      addMessage('assistant', response.data.results, 'search');
    } catch (error) {
      addMessage('assistant', 'Search failed. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:w-[440px] p-0 flex flex-col" data-testid="ai-assistant-panel">
        {/* Header */}
        <SheetHeader className="px-4 py-3 border-b border-border shrink-0">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2 text-base">
              <div className="p-1.5 rounded-lg bg-[#F5A623]/15">
                <Sparkles className="h-4 w-4 text-[#F5A623]" />
              </div>
              AI Assistant
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">Beta</Badge>
            </SheetTitle>
            {messages.length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearChat} className="h-7 text-xs" data-testid="clear-chat-btn">
                <Trash2 className="h-3 w-3 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </SheetHeader>

        {/* Quick Actions */}
        <div className="px-4 py-3 border-b border-border shrink-0">
          <p className="text-xs text-muted-foreground mb-2">Quick Actions</p>
          <div className="flex flex-wrap gap-2">
            <QuickAction icon={AlertTriangle} label="Predict Delays" onClick={handlePredictDelays} loading={loading} />
            <QuickAction icon={FileText} label="Summary" onClick={handleSummary} loading={loading} />
            <QuickAction icon={Search} label="Search" onClick={handleSearch} loading={loading} />
          </div>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 px-4" ref={scrollRef}>
          <div className="py-4 space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-12">
                <div className="p-3 rounded-full bg-[#F5A623]/10 w-fit mx-auto mb-3">
                  <Bot className="h-8 w-8 text-[#F5A623]" />
                </div>
                <p className="font-semibold">How can I help?</p>
                <p className="text-sm text-muted-foreground mt-1 max-w-[280px] mx-auto">
                  Ask about tasks, get summaries, predict delays, or search with natural language.
                </p>
                <div className="mt-4 space-y-2 text-xs text-muted-foreground">
                  <p className="italic">"What are my overdue tasks?"</p>
                  <p className="italic">"Summarize the Marketing department"</p>
                  <p className="italic">"Which tasks are at risk?"</p>
                  <p className="italic">"Create a task for Rohan due Friday — campaign brief"</p>
                </div>
              </div>
            )}
            
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-2.5 ${msg.role === 'user' ? 'justify-end' : ''}`} data-testid={`chat-message-${i}`}>
                {msg.role === 'assistant' && (
                  <div className="p-1.5 rounded-lg bg-[#F5A623]/10 h-fit shrink-0 mt-0.5">
                    <Bot className="h-3.5 w-3.5 text-[#F5A623]" />
                  </div>
                )}
                <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 ${
                  msg.role === 'user'
                    ? 'bg-[#F5A623] text-black'
                    : msg.type === 'error'
                      ? 'bg-red-500/10 border border-red-500/20'
                      : 'bg-accent'
                }`}>
                  {msg.role === 'user' ? (
                    <p className="text-sm">{msg.content}</p>
                  ) : (
                    <>
                      <MarkdownRenderer content={msg.content} />
                      {msg.proposedTask && (
                        <div className="mt-3 p-3 rounded-lg border border-[#F5A623]/40 bg-background space-y-1.5 text-xs" data-testid={`proposed-task-${i}`}>
                          <div className="flex items-center gap-1.5 font-semibold text-sm mb-1">
                            <Plus className="h-3.5 w-3.5 text-[#F5A623]" />
                            New Task Preview
                          </div>
                          <div><span className="text-muted-foreground">Name:</span> <strong>{msg.proposedTask.task_name}</strong></div>
                          <div><span className="text-muted-foreground">Due:</span> {msg.proposedTask.due_date || '—'}</div>
                          <div><span className="text-muted-foreground">Priority:</span> {msg.proposedTask.priority}</div>
                          <div><span className="text-muted-foreground">Department:</span> {msg.proposedTask.department}</div>
                          {msg.proposedTask.category && <div><span className="text-muted-foreground">Category:</span> {msg.proposedTask.category}</div>}
                          <div><span className="text-muted-foreground">Assignee:</span> {msg.proposedTask.assigned_to_name || 'You'}</div>
                          {msg.proposedTask.description && <div className="pt-1 italic text-muted-foreground">{msg.proposedTask.description}</div>}
                          {msg.taskCreated ? (
                            <div className="flex items-center gap-1.5 mt-2 text-emerald-600 dark:text-emerald-400 font-medium">
                              <CheckCircle2 className="h-3.5 w-3.5" /> Task created
                            </div>
                          ) : (
                            <Button
                              onClick={() => handleConfirmCreateTask(i, msg.proposedTask)}
                              size="sm"
                              className="mt-2 h-8 bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold w-full"
                              data-testid={`confirm-create-task-${i}`}
                            >
                              <Plus className="h-3.5 w-3.5 mr-1" /> Confirm & Create
                            </Button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
                {msg.role === 'user' && (
                  <div className="p-1.5 rounded-lg bg-slate-200 dark:bg-slate-700 h-fit shrink-0 mt-0.5">
                    <User className="h-3.5 w-3.5" />
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex gap-2.5" data-testid="ai-loading">
                <div className="p-1.5 rounded-lg bg-[#F5A623]/10 h-fit shrink-0">
                  <Bot className="h-3.5 w-3.5 text-[#F5A623]" />
                </div>
                <div className="bg-accent rounded-xl px-4 py-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Thinking...
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input */}
        <div className="px-4 py-3 border-t border-border shrink-0">
          <form onSubmit={handleSend} className="flex gap-2">
            <Textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Ask anything about your tasks..."
              rows={1}
              className="resize-none min-h-[40px] max-h-[100px]"
              disabled={loading}
              data-testid="ai-input"
            />
            <Button
              type="submit"
              size="icon"
              disabled={!input.trim() || loading}
              className="bg-[#F5A623] hover:bg-[#D48C1B] text-black shrink-0 h-10 w-10"
              data-testid="ai-send-btn"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
};
